/**
 * SocialOS API Client — Supabase-Compatible Interface
 *
 * All supabase.from(), supabase.auth, supabase.channel(), supabase.rpc(),
 * supabase.removeChannel(), supabase.functions.invoke() work as expected.
 *
 * Auth is backed by the real SDK client. Queries go through Cloudflare Workers API.
 * Realtime channels are stubbed (SDK WebSocket handles real-time).
 */

import { getClient } from './sdk/client';
import type {
  User as SdkUser,
  Session as SdkSession,
  UpdateProfileData,
} from './sdk/types';

// Re-export types for downstream consumers (AuthContext, hooks, etc.)
export type User = SdkUser;
export type Session = SdkSession;

// ============================================================
// API Base URL
// ============================================================
const API_BASE =
  (typeof window !== 'undefined'
    ? (window as unknown as Record<string, unknown>).VITE_API_BASE as string
    : undefined) ||
  import.meta.env.VITE_API_BASE ||
  'https://socialos-backend.bizme-top.workers.dev';

// ============================================================
// SDK-backed auth helpers
// ============================================================
function getSdk() {
  try {
    return getClient();
  } catch {
    return null;
  }
}

// ============================================================
// Query Builder (full chainable Supabase-compatible)
// ============================================================
interface QueryOp {
  type: string;
  [key: string]: unknown;
}

interface QueryState {
  table: string;
  operations: QueryOp[];
  singleMode: boolean;
  maybeSingleMode: boolean;
  countMode: 'exact' | 'planned' | 'estimated' | null;
  headMode: boolean;
  returningMode: boolean;
}

interface QueryBuilder {
  select: (columns?: string, options?: { count?: 'exact' | 'planned' | 'estimated'; head?: boolean }) => QueryBuilder;
  insert: (values: unknown) => QueryBuilder;
  update: (values: unknown) => QueryBuilder;
  upsert: (values: unknown, options?: { onConflict?: string; ignoreDuplicates?: boolean }) => QueryBuilder;
  delete: () => QueryBuilder;
  eq: (column: string, value: unknown) => QueryBuilder;
  neq: (column: string, value: unknown) => QueryBuilder;
  gt: (column: string, value: unknown) => QueryBuilder;
  lt: (column: string, value: unknown) => QueryBuilder;
  gte: (column: string, value: unknown) => QueryBuilder;
  lte: (column: string, value: unknown) => QueryBuilder;
  like: (column: string, value: unknown) => QueryBuilder;
  ilike: (column: string, value: unknown) => QueryBuilder;
  is: (column: string, value: unknown) => QueryBuilder;
  in: (column: string, values: unknown[]) => QueryBuilder;
  contains: (column: string, value: unknown) => QueryBuilder;
  overlaps: (column: string, value: unknown) => QueryBuilder;
  or: (filters: string) => QueryBuilder;
  range: (from: number, to: number) => QueryBuilder;
  order: (column: string, options?: { ascending?: boolean; nullsFirst?: boolean }) => QueryBuilder;
  limit: (count: number) => QueryBuilder;
  single: () => Promise<{ data: unknown; error: ApiError | null; count?: number }>;
  maybeSingle: () => Promise<{ data: unknown; error: ApiError | null; count?: number }>;
  then: <TResult1 = { data: unknown; error: ApiError | null; count?: number }, TResult2 = never>(
    onfulfilled?: ((value: { data: unknown; error: ApiError | null; count?: number }) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null
  ) => Promise<TResult1 | TResult2>;
}

interface ApiError {
  message: string;
  code?: string;
  status?: number;
}

function createQueryBuilder(table: string): QueryBuilder {
  const state: QueryState = {
    table,
    operations: [],
    singleMode: false,
    maybeSingleMode: false,
    countMode: null,
    headMode: false,
    returningMode: false,
  };

  const builder: QueryBuilder = {
    select: (columns = '*', options) => {
      state.operations.push({ type: 'select', columns });
      if (options?.count) state.countMode = options.count;
      if (options?.head) state.headMode = true;
      return builder;
    },
    insert: (values) => {
      state.operations.push({ type: 'insert', values });
      return builder;
    },
    update: (values) => {
      state.operations.push({ type: 'update', values });
      return builder;
    },
    upsert: (values, options) => {
      state.operations.push({ type: 'upsert', values, options });
      return builder;
    },
    delete: () => {
      state.operations.push({ type: 'delete' });
      return builder;
    },
    eq: (column, value) => {
      state.operations.push({ type: 'eq', column, value });
      return builder;
    },
    neq: (column, value) => {
      state.operations.push({ type: 'neq', column, value });
      return builder;
    },
    gt: (column, value) => {
      state.operations.push({ type: 'gt', column, value });
      return builder;
    },
    lt: (column, value) => {
      state.operations.push({ type: 'lt', column, value });
      return builder;
    },
    gte: (column, value) => {
      state.operations.push({ type: 'gte', column, value });
      return builder;
    },
    lte: (column, value) => {
      state.operations.push({ type: 'lte', column, value });
      return builder;
    },
    like: (column, value) => {
      state.operations.push({ type: 'like', column, value });
      return builder;
    },
    ilike: (column, value) => {
      state.operations.push({ type: 'ilike', column, value });
      return builder;
    },
    is: (column, value) => {
      state.operations.push({ type: 'is', column, value });
      return builder;
    },
    in: (column, values) => {
      state.operations.push({ type: 'in', column, values });
      return builder;
    },
    contains: (column, value) => {
      state.operations.push({ type: 'contains', column, value });
      return builder;
    },
    overlaps: (column, value) => {
      state.operations.push({ type: 'overlaps', column, value });
      return builder;
    },
    or: (filters) => {
      state.operations.push({ type: 'or', filters });
      return builder;
    },
    range: (from, to) => {
      state.operations.push({ type: 'range', from, to });
      return builder;
    },
    order: (column, options) => {
      state.operations.push({ type: 'order', column, options });
      return builder;
    },
    limit: (count) => {
      state.operations.push({ type: 'limit', count });
      return builder;
    },
    single: async () => {
      state.singleMode = true;
      const result = await executeQuery(state);
      return {
        data: Array.isArray(result.data) ? result.data[0] || null : result.data,
        error: result.error as ApiError | null,
        count: result.count,
      };
    },
    maybeSingle: async () => {
      state.maybeSingleMode = true;
      const result = await executeQuery(state);
      return {
        data: Array.isArray(result.data) ? result.data[0] || null : result.data,
        error: result.error as ApiError | null,
        count: result.count,
      };
    },
    then: async (onfulfilled, onrejected) => {
      try {
        const result = await executeQuery(state);
        const wrapped = {
          data: result.data,
          error: result.error as ApiError | null,
          count: result.count,
        };
        if (onfulfilled) {
          return onfulfilled(wrapped);
        }
        return wrapped as unknown as Awaited<ReturnType<NonNullable<typeof onfulfilled>>>;
      } catch (err) {
        if (onrejected) {
          return onrejected(err);
        }
        throw err;
      }
    },
  };

  return builder;
}

async function executeQuery(state: QueryState): Promise<{ data: unknown; error: unknown; count?: number }> {
  try {
    const body: Record<string, unknown> = { operations: state.operations };
    if (state.singleMode) body.single = true;
    if (state.maybeSingleMode) body.maybeSingle = true;
    if (state.countMode) body.count = state.countMode;
    if (state.headMode) body.head = true;
    if (state.returningMode) body.returning = true;
    if (state.headMode) body.returning = false;

    const response = await fetch(`${API_BASE}/db/${state.table}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await response.json();
    return {
      data: json.data ?? json,
      error: json.error ?? null,
      count: json.count,
    };
  } catch (error) {
    console.warn(`API call failed for ${state.table}:`, error);
    return { data: [], error };
  }
}

// ============================================================
// Auth (SDK-backed)
// ============================================================
type AuthCallback = (event: string, session: Session | null) => void;

const authStateListeners = new Set<AuthCallback>();

function notifyAuthListeners(event: string, session: Session | null) {
  authStateListeners.forEach((cb) => {
    try {
      cb(event, session);
    } catch {
      // ignore
    }
  });
}

const auth = {
  getSession: async () => {
    const sdk = getSdk();
    if (!sdk) return { data: { session: null }, error: null };
    const session = await sdk.getSession();
    return { data: { session }, error: null };
  },
  getUser: async () => {
    const sdk = getSdk();
    if (!sdk) return { data: { user: null }, error: null };
    const user = sdk.getCurrentUser();
    return { data: { user }, error: null };
  },
  signInWithPassword: async (credentials: { email: string; password: string }) => {
    const sdk = getSdk();
    if (!sdk) {
      // Fallback: direct API call
      const response = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });
      const data = await response.json();
      return { data, error: null };
    }
    try {
      const result = await sdk.signIn(credentials);
      notifyAuthListeners('SIGNED_IN', result.session);
      return { data: result, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  },
  signUp: async (credentials: { email: string; password: string; options?: { data?: Record<string, unknown>; emailRedirectTo?: string } }) => {
    const sdk = getSdk();
    if (!sdk) {
      const response = await fetch(`${API_BASE}/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials),
      });
      const data = await response.json();
      return { data, error: null };
    }
    try {
      const result = await sdk.signUp({
        email: credentials.email,
        password: credentials.password,
        name: credentials.options?.data?.name as string | undefined,
      });
      notifyAuthListeners('SIGNED_UP', result.session);
      return { data: result, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  },
  signOut: async () => {
    const sdk = getSdk();
    if (!sdk) {
      await fetch(`${API_BASE}/auth/logout`, { method: 'POST' });
      return { error: null };
    }
    try {
      await sdk.signOut();
      notifyAuthListeners('SIGNED_OUT', null);
      return { error: null };
    } catch (err) {
      return { error: err };
    }
  },
  resetPasswordForEmail: async (email: string, _options?: { redirectTo?: string }) => {
    const sdk = getSdk();
    if (!sdk) {
      const response = await fetch(`${API_BASE}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      return { data: await response.json(), error: null };
    }
    try {
      await sdk.resetPassword(email);
      return { data: {}, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  },
  updateUser: async (attributes: { password?: string; email?: string; data?: Record<string, unknown> }) => {
    const sdk = getSdk();
    if (!sdk) {
      const response = await fetch(`${API_BASE}/auth/update`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(attributes),
      });
      return { data: await response.json(), error: null };
    }
    try {
      const user = await sdk.updateProfile({
        name: attributes.data?.name as string | undefined,
        avatar_url: attributes.data?.avatar_url as string | undefined,
      } as UpdateProfileData);
      return { data: { user }, error: null };
    } catch (err) {
      return { data: null, error: err };
    }
  },
  onAuthStateChange: (callback: AuthCallback) => {
    authStateListeners.add(callback);

    // Also hook into SDK events if available
    const sdk = getSdk();
    let sdkUnsub: (() => void) | null = null;
    if (sdk) {
      sdkUnsub = sdk.on('sessionChanged', (session: unknown) => {
        const s = session as Session | null;
        if (s) {
          callback('SIGNED_IN', s);
          // Also fire INITIAL_SESSION on first load
          callback('INITIAL_SESSION', s);
        } else {
          callback('SIGNED_OUT', null);
        }
      });
    }

    return {
      data: {
        subscription: {
          unsubscribe: () => {
            authStateListeners.delete(callback);
            if (sdkUnsub) sdkUnsub();
          },
        },
      },
    };
  },
};

// ============================================================
// Storage
// ============================================================
const storage = {
  from: (bucket: string) => ({
    upload: async (path: string, file: File | Blob) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_BASE}/storage/${bucket}/${path}`, {
        method: 'POST',
        body: formData,
      });
      return { data: await response.json(), error: null };
    },
    getPublicUrl: (path: string) => ({
      data: { publicUrl: `${API_BASE}/storage/${bucket}/${path}` },
    }),
    list: async (path?: string) => {
      const response = await fetch(`${API_BASE}/storage/${bucket}/list?path=${path || ''}`);
      return { data: await response.json(), error: null };
    },
    remove: async (paths: string[]) => {
      await fetch(`${API_BASE}/storage/${bucket}/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paths }),
      });
      return { error: null };
    },
  }),
};

// ============================================================
// Realtime Channels (stub — SDK WebSocket handles real-time)
// ============================================================
interface RealtimeChannel {
  on: (event: string, filter: Record<string, unknown>, callback: (payload: unknown) => void) => RealtimeChannel;
  subscribe: (callback?: (status: string, err?: Error) => void) => RealtimeChannel;
  unsubscribe: () => void;
  send: (message: { type: string; event: string; payload: unknown }) => Promise<void>;
  track: (payload?: Record<string, unknown>) => void;
  untrack: () => void;
}

// In-memory channel registry for broadcasting and tracking
const channelRegistry = new Map<string, Set<{ event: string; filter: Record<string, unknown>; callback: (payload: unknown) => void }>>();

// Tracked subscriptions for realtime channels
const trackedChannels = new Map<string, { payload: Record<string, unknown>; channel: RealtimeChannel }>();

function createChannel(name: string, _options?: Record<string, unknown>): RealtimeChannel {
  const listeners: Set<{ event: string; filter: Record<string, unknown>; callback: (payload: unknown) => void }> = new Set();
  channelRegistry.set(name, listeners);

  let trackedPayload: Record<string, unknown> | null = null;

  const channel: RealtimeChannel = {
    on(event: string, filter: Record<string, unknown>, callback: (payload: unknown) => void) {
      listeners.add({ event, filter, callback });
      return channel;
    },
    subscribe(callback?: (status: string, err?: Error) => void) {
      // Actually connect to the backend WebSocket for this channel
      const sdk = getSdk();
      if (sdk) {
        sdk.connectWebSocket();
        if (trackedPayload) {
          // Send tracking data over the real WebSocket
          sdk.sendWebSocketEvent('track', {
            channel: name,
            payload: trackedPayload,
          });
        }
      }

      if (callback) {
        setTimeout(() => callback('SUBSCRIBED'), 10);
      }
      return channel;
    },
    unsubscribe() {
      listeners.clear();
      channelRegistry.delete(name);
      trackedChannels.delete(name);

      // Disconnect from backend tracking
      const sdk = getSdk();
      if (sdk && trackedPayload) {
        sdk.sendWebSocketEvent('untrack', { channel: name });
      }
    },
    async send(message: { type: string; event: string; payload: unknown }) {
      // Broadcast to all listeners on this channel
      listeners.forEach(({ event: listenEvent, filter, callback }) => {
        if (message.type === 'broadcast' && listenEvent === 'broadcast') {
          if (filter.event === message.event || !filter.event) {
            try {
              callback({ payload: message.payload, event: message.event });
            } catch {
              // ignore
            }
          }
        }
      });

      // Also send through real SDK WebSocket
      const sdk = getSdk();
      if (sdk) {
        sdk.sendWebSocketEvent(message.event, message.payload);
      }
    },
    track(payload?: Record<string, unknown>) {
      trackedPayload = payload || {};
      trackedChannels.set(name, { payload: trackedPayload, channel });

      // Send tracking through real SDK WebSocket
      const sdk = getSdk();
      if (sdk) {
        sdk.connectWebSocket();
        sdk.sendWebSocketEvent('track', {
          channel: name,
          payload: trackedPayload,
        });
      }
    },
    untrack() {
      trackedPayload = null;
      trackedChannels.delete(name);

      // Send untrack through real SDK WebSocket
      const sdk = getSdk();
      if (sdk) {
        sdk.sendWebSocketEvent('untrack', { channel: name });
      }
    },
  };

  return channel;
}

const realtime = {
  channel: createChannel,
  removeChannel: (channel: RealtimeChannel) => {
    channel.unsubscribe();
  },
  removeAllChannels: () => {
    channelRegistry.forEach((listeners) => listeners.clear());
    channelRegistry.clear();
  },
};

// ============================================================
// Functions (Edge Functions / Stripe, etc.)
// ============================================================
const functions = {
  invoke: async (name: string, options?: { method?: string; body?: unknown }) => {
    const response = await fetch(`${API_BASE}/functions/${name}`, {
      method: options?.method || 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: options?.body ? JSON.stringify(options.body) : undefined,
    });
    return { data: await response.json(), error: null };
  },
};

// ============================================================
// RPC (stored procedures / PostGIS)
// ============================================================
async function rpc(fn: string, params?: Record<string, unknown>) {
  try {
    const response = await fetch(`${API_BASE}/rpc/${fn}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params || {}),
    });
    const json = await response.json();
    return { data: json, error: null };
  } catch (error) {
    console.warn(`RPC call failed for ${fn}:`, error);
    return { data: null, error };
  }
}

// ============================================================
// Main supabase export
// ============================================================
export const supabase = {
  from: (table: string) => createQueryBuilder(table),
  auth,
  storage,
  channel: realtime.channel,
  removeChannel: realtime.removeChannel,
  removeAllChannels: realtime.removeAllChannels,
  functions,
  rpc,
};

// ============================================================
// SDK exports (for other files that import from here)
// ============================================================
export { getClient as getSdkClient } from './sdk/client';

export type {
  ApiResponse,
  ApiError as SdkApiError,
  UpdateProfileData,
  QueryOptions,
  WebSocketEventType,
  WebSocketConnectionState,
} from './sdk/types';
